import React, { useState, useEffect } from 'react';
import { Modal, Button, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useForm } from 'react-hook-form';
import useCreateFaq from '../../../hooks/catalog/faqs/useCreateFaq';
import { useIntersectionObserver } from 'usehooks-ts';
import { debounce } from 'lodash';
import useGet from '../../../hooks/useGet';
import Input from '../../../components/elements/inputs/Input';

const CreateFaqModal = ({ open, handleClose }) => {
  // Form handling
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { handleSubmit: createFaq } = useCreateFaq({ reset });
  
  // Product selection
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  
  // API call for products
  const {
    data: productsData,
    setParamsObject,
    data: productsQuery,
  } = useGet({
    url: 'product/list',
    initialParams: {
      pageNo: 0,
      limit: 10,
      searchQuery: '',
    },
    onSuccess: (response) => {
      const newProducts = response?.data || [];
      const total_count = response?.total_count || 0;
      
      if (pageNo === 0) {
        setProducts(newProducts);
      } else {
        setProducts((prev) => [...prev, ...newProducts]);
      }
      
      const updatedProductCount = pageNo === 0 
        ? newProducts.length 
        : products.length + newProducts.length;
      
      setHasMore(updatedProductCount < total_count);
    },
  });
  
  // Set up intersection observer for infinite scrolling
  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && hasMore && !productsQuery?.isFetching) {
        loadMoreProducts();
      }
    },
  });
  
  // Load more products when scrolling down
  const loadMoreProducts = () => {
    setPageNo((prev) => prev + 1);
    setParamsObject({
      pageNo: pageNo + 1,
      limit: 10,
      searchQuery: searchQuery,
    });
  };
  
  // Debounced search function
  const debouncedSearch = React.useRef(
    debounce((query) => {
      setSearchQuery(query);
      setPageNo(0); // Reset to first page when searching
      setParamsObject({
        pageNo: 0,
        limit: 10,
        searchQuery: query,
      });
    }, 500)
  ).current;
  
  // Handle search input change
  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };
  
  // Reset when modal opens
  useEffect(() => {
    if (open) {
      reset();
      setSelectedProducts([]);
      setPageNo(0);
      setSearchQuery('');
      setParamsObject({
        pageNo: 0,
        limit: 10,
        searchQuery: '',
      });
    }
  }, [open, reset, setParamsObject]);
  
  // Toggle product selection
  const handleToggleProduct = (product) => {
    setSelectedProducts((prev) => {
      const isSelected = prev.some(p => p.id === product.id);
      
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };
  
  // Submit form
  const onSubmit = (data) => {
    const formData = {
      ...data,
      products: selectedProducts.map(p => p.id)
    };
    createFaq(formData);
    handleClose();
  };
  
  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      aria-labelledby="create-faq-modal"
    >
      <div className="absolute top-1/2 left-1/2 bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Create New FAQ</h2>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 mb-6">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="question">
                Question
              </label>
              <input
                id="question"
                type="text"
                placeholder="Enter question"
                className={`appearance-none border ${errors.question ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-blue-500`}
                {...register('question', { required: 'Question is required' })}
              />
              {errors.question && (
                <p className="text-red-500 text-xs mt-1">{errors.question.message}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="answer">
                Answer
              </label>
              <textarea
                id="answer"
                rows="4"
                placeholder="Enter answer"
                className={`appearance-none border ${errors.answer ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-blue-500`}
                {...register('answer', { required: 'Answer is required' })}
              />
              {errors.answer && (
                <p className="text-red-500 text-xs mt-1">{errors.answer.message}</p>
              )}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Map Products to FAQ</h3>
            <p className="text-sm text-gray-500 mb-3">
              Select products that this FAQ applies to
            </p>
            
            {/* Product Search */}
            <div className="mb-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search products..."
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 w-full border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            
            {/* Products List */}
            <div className="border rounded-md overflow-hidden max-h-60 overflow-y-auto">
              {productsQuery?.isLoading && products.length === 0 ? (
                <div className="flex justify-center items-center p-4">
                  <CircularProgress size={24} />
                </div>
              ) : products.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No products found
                </div>
              ) : (
                <>
                  {products.map((product) => {
                    const isSelected = selectedProducts.some(p => p.id === product.id);
                    return (
                      <div
                        key={product.id}
                        className={`flex items-center p-3 border-b cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleToggleProduct(product)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{product.product_name}</p>
                          <p className="text-xs text-gray-500">{product.brand_name}</p>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Load more trigger */}
                  {hasMore && (
                    <div ref={ref} className="p-2 text-center">
                      {productsQuery?.isFetching && (
                        <CircularProgress size={20} />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Selected Products Chips */}
            {selectedProducts.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedProducts.map(product => (
                  <div 
                    key={product.id} 
                    className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center"
                  >
                    {product.product_name}
                    <button 
                      type="button"
                      className="ml-1.5 text-blue-800 hover:text-blue-900"
                      onClick={() => handleToggleProduct(product)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outlined" 
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
            >
              Create FAQ
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateFaqModal;
